/**
 * Oila Admin Panel — login/parol, barcha tadbirlar, 1 ta QR, media boshqaruv.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  LogOut,
  QrCode,
  Download,
  Printer,
  Trash2,
  Eye,
  EyeOff,
  Archive,
  ExternalLink,
  Images,
  Video,
  MessageCircle,
  ArrowLeft,
  Tv,
} from "lucide-react";
import "../oilaTheme.css";

const TOKEN_KEY = "oila_admin_token";

interface EventRow {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  event_date: string;
  status: string;
  media_count: number;
  host_pin: string;
  guest_url: string;
}

interface MediaItem {
  id: string;
  media_type: "image" | "video";
  guest_name: string;
  url: string;
  thumb_url: string;
  status?: string;
  show_on_live?: boolean;
}

interface MessageItem {
  id: string;
  guest_name: string;
  text: string;
}

interface QrTexts {
  brand: string;
  title: string;
  subtitle: string;
  date_text: string;
  instruction: string;
  help_text: string;
  footer: string;
}

const DEFAULT_QR_TEXTS: QrTexts = {
  brand: "✦  OILA  ✦",
  title: "",
  subtitle: "",
  date_text: "",
  instruction: "Telefon bilan skanerlang",
  help_text: "Surat va video yuklang",
  footer: "oila.lifegram.uz",
};

async function api(path: string, opts: RequestInit & { token?: string } = {}) {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.body ? { "Content-Type": "application/json" } : {}),
    ...(opts.headers as Record<string, string>),
  };
  if (opts.token) headers["X-Oila-Admin-Token"] = opts.token;
  const res = await fetch(`/api/v1/oila${path}`, { ...opts, headers });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json?.errors?.[0]?.message || `Xato ${res.status}`);
  }
  return json;
}

export default function AdminPanelPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [events, setEvents] = useState<EventRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<{
    event: EventRow & { live_wall_enabled?: boolean };
    stats: Record<string, number>;
    primary_qr: { qr_base64: string; url: string; texts?: QrTexts };
    media: MediaItem[];
    messages: MessageItem[];
  } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", subtitle: "", slug: "", event_date: "" });
  const [qrTexts, setQrTexts] = useState<QrTexts>(DEFAULT_QR_TEXTS);
  const [savingQr, setSavingQr] = useState(false);

  const loadEvents = useCallback(async (t: string) => {
    const j = await api("/admin/events/", { token: t });
    setEvents(j.data.events);
  }, []);

  const loadDetail = useCallback(async (slug: string, t: string) => {
    const j = await api(`/admin/events/${slug}/`, { token: t });
    setDetail(j.data);
    setSelected(slug);
    const texts = j.data?.primary_qr?.texts;
    if (texts) {
      setQrTexts({ ...DEFAULT_QR_TEXTS, ...texts });
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    loadEvents(token).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    });
  }, [token, loadEvents]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const j = await api("/admin/login/", {
        method: "POST",
        body: JSON.stringify({ login, password }),
      });
      localStorage.setItem(TOKEN_KEY, j.data.token);
      setToken(j.data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kirish xato");
    }
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSelected(null);
    setDetail(null);
  }

  async function createEvent(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const j = await api("/admin/events/", {
        method: "POST",
        token,
        body: JSON.stringify({ ...form, tables: [] }),
      });
      setShowCreate(false);
      setForm({ title: "", subtitle: "", slug: "", event_date: "" });
      await loadEvents(token);
      await loadDetail(j.data.slug, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Yaratish xato");
    } finally {
      setCreating(false);
    }
  }

  async function archiveEvent(slug: string) {
    if (!confirm("Tadbirni arxivlaysizmi? Mehmonlar endi yuklay olmaydi.")) return;
    await api(`/admin/events/${slug}/`, {
      method: "PATCH",
      token,
      body: JSON.stringify({ status: "archived" }),
    });
    await loadEvents(token);
    if (selected === slug) await loadDetail(slug, token);
  }

  async function toggleMedia(m: MediaItem, action: "live" | "hide" | "delete") {
    if (!selected) return;
    if (action === "delete") {
      if (!confirm("O‘chirilsinmi?")) return;
      await api(`/admin/events/${selected}/media/${m.id}/`, { method: "DELETE", token });
    } else if (action === "live") {
      await api(`/admin/events/${selected}/media/${m.id}/`, {
        method: "PATCH",
        token,
        body: JSON.stringify({ show_on_live: !m.show_on_live }),
      });
    } else {
      await api(`/admin/events/${selected}/media/${m.id}/`, {
        method: "PATCH",
        token,
        body: JSON.stringify({ status: m.status === "hidden" ? "ready" : "hidden" }),
      });
    }
    await loadDetail(selected, token);
    await loadEvents(token);
  }

  async function deleteMessage(id: string) {
    if (!selected || !confirm("Tabrikni o‘chirilsinmi?")) return;
    await api(`/admin/events/${selected}/messages/${id}/`, { method: "DELETE", token });
    await loadDetail(selected, token);
  }

  function downloadQr() {
    if (!detail?.primary_qr?.qr_base64) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${detail.primary_qr.qr_base64}`;
    a.download = `oila-qr-${selected}.png`;
    a.click();
  }

  async function saveQrTexts(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !token) return;
    setSavingQr(true);
    setError("");
    try {
      const j = await api(`/admin/events/${selected}/`, {
        method: "PATCH",
        token,
        body: JSON.stringify({ qr_texts: qrTexts }),
      });
      if (j.data?.primary_qr) {
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                primary_qr: j.data.primary_qr,
                event: { ...prev.event, ...j.data },
              }
            : prev
        );
        if (j.data.primary_qr.texts) {
          setQrTexts({ ...DEFAULT_QR_TEXTS, ...j.data.primary_qr.texts });
        }
      } else {
        await loadDetail(selected, token);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "QR saqlash xato");
    } finally {
      setSavingQr(false);
    }
  }

  // ── Login ──
  if (!token) {
    return (
      <div className="oila-root min-h-screen flex items-center justify-center p-6">
        <form onSubmit={handleLogin} className="oila-card w-full max-w-md p-8 space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#d4a745] to-[#8b6914] flex items-center justify-center mb-4">
              <QrCode className="text-[#1a1610]" size={28} />
            </div>
            <h1 className="text-2xl font-bold text-[#f5f0e8]">Oila Admin</h1>
            <p className="text-[#a09585] text-sm mt-1">Barcha tadbirlarni boshqarish</p>
          </div>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder="Login"
            autoComplete="username"
            className="w-full px-4 py-3.5 rounded-xl bg-[#1a1610] border border-[#d4a745]/30 text-[#f5f0e8] text-lg"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Parol"
            autoComplete="current-password"
            className="w-full px-4 py-3.5 rounded-xl bg-[#1a1610] border border-[#d4a745]/30 text-[#f5f0e8] text-lg"
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button
            type="submit"
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#d4a745] to-[#b8860b] text-[#1a1610] font-bold text-lg"
          >
            Kirish
          </button>
        </form>
      </div>
    );
  }

  // ── Detail view ──
  if (selected && detail) {
    const e = detail.event;
    return (
      <div className="oila-root min-h-screen">
        <div className="max-w-5xl mx-auto px-4 py-6 pb-16">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setDetail(null);
              }}
              className="flex items-center gap-2 text-[#d4a745] font-medium"
            >
              <ArrowLeft size={18} /> Orqaga
            </button>
            <div className="flex-1" />
            <Link
              to={`/${e.slug}/live`}
              className="px-3 py-2 rounded-xl border border-[#d4a745]/30 text-sm flex items-center gap-1.5"
            >
              <Tv size={14} /> Devor
            </Link>
            <Link
              to={`/${e.slug}/albom`}
              className="px-3 py-2 rounded-xl border border-[#d4a745]/30 text-sm flex items-center gap-1.5"
            >
              <Images size={14} /> Albom
            </Link>
            <a
              href={`/${e.slug}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-xl border border-[#d4a745]/30 text-sm flex items-center gap-1.5"
            >
              <ExternalLink size={14} /> Mehmon
            </a>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-[#f5f0e8] mb-1">{e.title}</h1>
          <p className="text-[#a09585] mb-6">
            {e.event_date} · {e.status === "archived" ? "Arxiv" : "Faol"} · PIN:{" "}
            <span className="font-mono text-[#d4a745]">{e.host_pin}</span>
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { label: "Media", value: detail.stats.total_media, icon: Images },
              { label: "Surat", value: detail.stats.images, icon: Images },
              { label: "Video", value: detail.stats.videos, icon: Video },
              { label: "Tabrik", value: detail.stats.messages, icon: MessageCircle },
            ].map((s) => (
              <div key={s.label} className="oila-card p-4 text-center">
                <s.icon className="mx-auto text-[#d4a745] mb-1" size={18} />
                <p className="text-2xl font-bold text-[#f5f0e8]">{s.value ?? 0}</p>
                <p className="text-xs text-[#a09585]">{s.label}</p>
              </div>
            ))}
          </div>

          {/* QR */}
          <section className="oila-card p-6 mb-8">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <h2 className="font-semibold text-[#f5f0e8] flex items-center gap-2">
                <QrCode size={20} className="text-[#d4a745]" /> Bitta QR kod
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={downloadQr}
                  className="px-4 py-2 rounded-xl bg-[#1a1610] border border-[#d4a745]/40 text-[#d4a745] text-sm font-semibold flex items-center gap-2"
                >
                  <Download size={16} /> Rasm yuklash
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#d4a745] to-[#b8860b] text-[#1a1610] text-sm font-bold flex items-center gap-2"
                >
                  <Printer size={16} /> Chop etish
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div>
                <div className="flex justify-center">
                  <img
                    src={`data:image/png;base64,${detail.primary_qr.qr_base64}`}
                    alt="QR"
                    className="w-full max-w-xs rounded-2xl shadow-2xl"
                  />
                </div>
                <p className="text-center text-sm text-[#a09585] mt-3 break-all">{detail.primary_qr.url}</p>
              </div>

              <form onSubmit={saveQrTexts} className="space-y-3">
                <h3 className="text-[#d4a745] font-semibold text-sm">QR yozuvlarini tahrirlash</h3>
                {(
                  [
                    ["brand", "Yuqori brend"],
                    ["title", "Sarlavha"],
                    ["subtitle", "Qo‘shimcha matn"],
                    ["date_text", "Sana / qator"],
                    ["instruction", "Asosiy ko‘rsatma"],
                    ["help_text", "Pastki yordam matni"],
                    ["footer", "Footer"],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="text-xs text-[#a09585] mb-1 block">{label}</span>
                    <input
                      value={qrTexts[key]}
                      onChange={(ev) => setQrTexts((prev) => ({ ...prev, [key]: ev.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl bg-[#1a1610] border border-[#d4a745]/25 text-[#f5f0e8] text-sm"
                    />
                  </label>
                ))}
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={savingQr}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#d4a745] to-[#b8860b] text-[#1a1610] font-bold text-sm disabled:opacity-60"
                >
                  {savingQr ? "Saqlanmoqda…" : "Saqlash va QR yangilash"}
                </button>
              </form>
            </div>
          </section>

          {/* Messages */}
          {detail.messages.length > 0 && (
            <section className="oila-card p-5 mb-8">
              <h2 className="font-semibold text-[#f5f0e8] mb-3">Tabriklar</h2>
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {detail.messages.map((m) => (
                  <li
                    key={m.id}
                    className="flex gap-3 items-start justify-between p-3 rounded-xl bg-[#1a1610]/50"
                  >
                    <div>
                      <p className="text-[#f5f0e8] text-sm">{m.text}</p>
                      <p className="text-xs text-[#a09585] mt-1">{m.guest_name || "Mehmon"}</p>
                    </div>
                    <button type="button" onClick={() => deleteMessage(m.id)} className="text-red-400 p-1">
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Media grid */}
          <section className="oila-card p-5 mb-6">
            <h2 className="font-semibold text-[#f5f0e8] mb-4">Media</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {detail.media.map((m) => (
                <div
                  key={m.id}
                  className={`relative aspect-square rounded-xl overflow-hidden bg-[#1a1610] ${
                    m.status === "hidden" ? "opacity-40" : ""
                  }`}
                >
                  {m.media_type === "video" ? (
                    <video src={m.url} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={m.thumb_url || m.url} alt="" className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 p-2 flex justify-center gap-2">
                    <button type="button" onClick={() => toggleMedia(m, "live")} className="p-1.5 bg-white/90 rounded-full">
                      {m.show_on_live ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button type="button" onClick={() => toggleMedia(m, "hide")} className="p-1.5 bg-white/90 rounded-full text-xs px-2">
                      {m.status === "hidden" ? "Ko‘rsat" : "Yashir"}
                    </button>
                    <button type="button" onClick={() => toggleMedia(m, "delete")} className="p-1.5 bg-red-500 text-white rounded-full">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {!detail.media.length && <p className="text-center text-[#a09585] py-8">Hali media yo‘q</p>}
          </section>

          {e.status !== "archived" && (
            <button
              type="button"
              onClick={() => archiveEvent(e.slug)}
              className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 flex items-center justify-center gap-2"
            >
              <Archive size={16} /> Tadbirni arxivlash
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── List ──
  return (
    <div className="oila-root min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#f5f0e8]">Oila Admin</h1>
            <p className="text-sm text-[#a09585]">{events.length} ta tadbir</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#d4a745] to-[#b8860b] text-[#1a1610] font-bold text-sm flex items-center gap-1.5"
            >
              <Plus size={16} /> Yangi
            </button>
            <button type="button" onClick={logout} className="px-3 py-2.5 text-[#a09585]">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {showCreate && (
          <form onSubmit={createEvent} className="oila-card p-5 mb-6 space-y-3">
            <h2 className="font-semibold text-[#f5f0e8]">Yangi to‘y / tadbir</h2>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sarlavha: Sanobar & Ali"
              className="w-full px-4 py-3 rounded-xl bg-[#1a1610] border border-[#d4a745]/25 text-[#f5f0e8]"
            />
            <input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="Qo‘shimcha matn"
              className="w-full px-4 py-3 rounded-xl bg-[#1a1610] border border-[#d4a745]/25 text-[#f5f0e8]"
            />
            <input
              required
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })
              }
              placeholder="havola: sanobar-ali"
              className="w-full px-4 py-3 rounded-xl bg-[#1a1610] border border-[#d4a745]/25 text-[#f5f0e8] font-mono"
            />
            <input
              required
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[#1a1610] border border-[#d4a745]/25 text-[#f5f0e8]"
            />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={creating}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#d4a745] to-[#b8860b] text-[#1a1610] font-bold"
              >
                {creating ? "…" : "Yaratish + QR"}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-3 rounded-xl border border-[#d4a745]/30 text-[#a09585]"
              >
                Bekor
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {events.map((ev) => (
            <button
              key={ev.id}
              type="button"
              onClick={() => loadDetail(ev.slug, token)}
              className="w-full oila-card p-4 text-left hover:border-[#d4a745]/50 transition border border-transparent"
            >
              <div className="flex justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#f5f0e8] text-lg">{ev.title}</p>
                  <p className="text-sm text-[#a09585] mt-0.5">
                    {ev.event_date} · {ev.media_count} media · /{ev.slug}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-xs px-2 py-1 rounded-full h-fit ${
                    ev.status === "active"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-stone-500/20 text-stone-400"
                  }`}
                >
                  {ev.status === "active" ? "Faol" : "Arxiv"}
                </span>
              </div>
            </button>
          ))}
          {!events.length && (
            <p className="text-center text-[#a09585] py-16">Hali tadbir yo‘q. “Yangi” bosing.</p>
          )}
        </div>
      </div>
    </div>
  );
}
